import {CBARObject} from "./CBARObject";
import * as THREE from "three";
import {CBARTexture} from "./CBARTexture";
import {Texture} from "three";
import {CBARError, CBARErrorCode} from "../CBARError";
import {CBARCollection} from "../CBARCollection";
import {parseIncludes} from "../internal/Utils";
import {CBARContext} from "../CBARContext";
import {ImageCrop} from "cambrian-base";
import {Point2D} from "../CBARTypes";
import {
    MaskFunctions,
    MaskUniforms,
    SerializeUniforms,
    setShaderUniforms, StandardUniforms,
    UniformedDictionary
} from "./CBARShaderFunctions";
import {iCBARMaskedMaterial} from "./iCBARMaskedMaterial";

export enum CBARTextureType {
    albedo = 'albedo',
    normals = 'normals',
    bump = 'bump',
    roughness = 'roughness',

    lightMap = 'lightMap',
    alpha = 'alpha',
    emissive = 'emissive',
    displacement = 'displacement',
    metalness = 'metalness',
    environment = 'environment'
}

export enum CBARMaterialProperty {
    color = 'color',
    opacity = 'opacity',

    roughnessValue = 'roughnessValue',
    metalnessValue = 'metalnessValue',
    bumpScaleValue = 'bumpScaleValue',

    lightMapIntensity = 'lightMapIntensity',
    aoMapIntensity =  'aoMapIntensity',
    emissiveIntensity = 'emissiveIntensity',
    displacementScale = 'displacementScale',
    displacementBias = 'displacementBias',
    envMapIntensity =  'envMapIntensity',
}

export interface CBARMaterialProperties {
    ppi?:number,
    ppcm?:number,
    scale?:number,
    mirrored?:boolean,
    mirroredX?:boolean,
    mirroredY?:boolean,
    repeat?:boolean,
    crop?: ImageCrop
    textures?: Record<string, string>
    properties?: Record<string, any>

    lightingFactor?:number
    smoothStep?:number
    lightingOffset?:number
}

export type ShaderUniform = {
    type:string
    value:any
}

export abstract class CBARMaterial<T> extends CBARObject<CBARMaterial<T>> implements iCBARMaskedMaterial, CBARCollection<CBARTexture> {

    protected constructor(context:CBARContext, reference?: CBARMaterial<T>) {
        super(context);

        //attach uniforms
        this.uniforms = StandardUniforms(this.context.gl.resolution);
    }

    public readonly uniforms:UniformedDictionary;

    protected abstract cloneObject() : CBARMaterial<T>

    protected _material?:THREE.Material;

    public wrappingX = THREE.RepeatWrapping;
    public wrappingY = THREE.RepeatWrapping;

    public abstract get threeMaterial() : THREE.Material

    public setThreeMaterial(value:THREE.Material) {
        this._material = value;
        this._material.onBeforeCompile = (shader) => {
            this.onBeforeCompileCallback(shader);
            this._shader = shader;
        }
    }

    protected get isPaint() {
        return false;
    };

    private _shader?:THREE.Shader

    protected onBeforeCompileCallback(shader:THREE.Shader) {

        shader.fragmentShader = parseIncludes(shader.fragmentShader);

        const headers = [SerializeUniforms(this.uniforms), ...MaskFunctions]

        shader.fragmentShader = headers.join('\n') + shader.fragmentShader;

        setShaderUniforms(shader, this.uniforms);

        const textureName = 'alphaMap';
        //TODO: vUv * vUv2 * vec2(0.0) is a bad way to disable warnings:
        // gl.getProgramInfoLog() WARNING: Output of vertex shader 'vUv' not read by fragment shader etc.
        // so find the correct way
        shader.fragmentShader = shader.fragmentShader.replace(
            new RegExp(`diffuseColor.a \\*= texture2D\\( *${textureName}, *vUv *\\)\\.g;`),
            `
                vec2 pos = clamp(vUv * vUv2 * vec2(0.0) + gl_FragCoord.xy / u_viewportSize, 0.0, 1.0);
                vec2 maskPos = cropPosition(pos, u_maskCrop);
                vec2 blendRadius = u_maskBlendDistance / u_viewportSize;
                diffuseColor.a = maskValue(${textureName}, maskPos, blendRadius, u_maskIndex);`);

        //switch variables to use screenPosition
        //make lightmap use screen uv coordinates
        const hasLightMap = (this.threeMaterial as any).hasOwnProperty("lightMap");
        if (hasLightMap)
        {
            const textureName = 'lightMap';
            shader.fragmentShader = shader.fragmentShader.replace(
                new RegExp(`texture2D\\( *${textureName}, *vUv2 *\\)`),
                `texture2D( ${textureName}, pos)`)
        }

        //make metalness map use screen uv coordinates
        const hasMetalness = (this.threeMaterial as any).hasOwnProperty("metalnessMap");
        if (hasMetalness)
        {
            const textureName = 'metalnessMap';
            shader.fragmentShader = shader.fragmentShader.replace(
                new RegExp(`texture2D\\( *${textureName}, *vUv *\\)`),
                `texture2D( ${textureName}, pos)`);
        }

        //ignore lightmap at irradiance
        if (hasLightMap) {

            if (this.isPaint) {
                shader.fragmentShader = shader.fragmentShader.replace(/#endif\s+#ifdef DITHERING/gi,`
                        #endif
                        //value of bg contributes x%: Color itself brightens or darkens a room
                        float luminance = clamp(u_lightingFactor * lightMapIrradiance.g + u_lightingOffset, 0.0, 1.0);
                        gl_FragColor.rgb = material.diffuseColor.rgb * luminance;
                        #ifdef DITHERING`)

            } else {
                shader.fragmentShader = shader.fragmentShader.replace(/#endif\s+#ifdef DITHERING/gi,`
                    #endif
                    
                    vec3 luminance = lightMapIrradiance;
                    gl_FragColor.rgb = gl_FragColor.rgb * luminance;
                    #ifdef DITHERING`)
            }
        }

        //console.log(`Shader hasAlpha:${hasAlpha}, hasLightMap:${hasLightMap}, hasMetalness:${hasMetalness}`)

        //console.log("fragment shader", shader.fragmentShader);

        //this.logUniforms()
    }

    protected setTextureMap(texture:CBARTexture) : Texture | null {
        return null
    }

    private _materialProperties: { [property: string] : number; } = {};

    public getMaterialProperty(name:CBARMaterialProperty) : any {
        return this._materialProperties[name]
    }

    public setMaterialProperty(name:CBARMaterialProperty, value:any) {
        this._materialProperties[name] = value
    }

    public getUniform(name:string) : any {
        return this._shader ? this._shader.uniforms[name].value : this.uniforms[name].value;
    }

    public setUniform(name:string, value:any) {
        if (this._shader) {
            return this._shader.uniforms[name].value = value
        } else {
            this.uniforms[name].value = value;
        }
    }

    public get lightingFactor() : number {
        return this.getUniform("u_lightingFactor");
    }

    public set lightingFactor(value:number) {
        this.setUniform("u_lightingFactor", value);
    }

    public get lightingOffset() : number {
        return this.getUniform("u_lightingOffset");
    }

    public set lightingOffset(value:number) {
        this.setUniform("u_lightingOffset", value);
    }

    public get smoothStep() : number {
        return this.getUniform("u_smoothStep");
    }

    public set smoothStep(value:number) {
        this.setUniform("u_smoothStep", value);
    }

    public get maskIndex() : number {
        return this.getUniform("u_maskIndex");
    }

    public set maskIndex(value:number) {
        this.setUniform("u_maskIndex", value);
    }

    public get maskBlendDistance() : number {
        return this.getUniform("u_maskBlendDistance");
    }

    public set maskBlendDistance(value:number) {
        this.setUniform("u_maskBlendDistance", value);
    }

    public get maskCrop() : THREE.Vector4 {
        return this.getUniform("u_maskCrop");
    }

    public set maskCrop(value:THREE.Vector4) {
        this.setUniform("u_maskCrop", value);
    }

    public ppcm = 20 / 2.54;
    public scale = 1.0;

    public get ppi() : number {
        return this.ppcm * 2.54
    }

    public set ppi(value:number) {
        this.ppcm = value / 2.54
    }

    public get pixelsPerMeter() : number {
        return this.ppcm * 100.0 / this.scale;
    }

    public get imageDimensions() : THREE.Vector2 | undefined {
        const albedo = this.values[CBARTextureType.albedo];
        if (albedo) {
            return albedo.imageDimensions
        }
        return undefined
    }

    public get physicalDimensions() : THREE.Vector2 | undefined {
        if (this.imageDimensions) {
            return new THREE.Vector2( this.imageDimensions.x / this.pixelsPerMeter, this.imageDimensions.y / this.pixelsPerMeter)
        }
        return undefined
    }

    public get mirroredX() : boolean {
        return this.wrappingX === THREE.MirroredRepeatWrapping;
    }

    public set mirroredX(value:boolean) {
        this.wrappingX = value ? THREE.MirroredRepeatWrapping : THREE.RepeatWrapping;
    }

    public get mirroredY() : boolean {
        return this.wrappingY === THREE.MirroredRepeatWrapping;
    }

    public set mirroredY(value:boolean) {
        this.wrappingY = value ? THREE.MirroredRepeatWrapping : THREE.RepeatWrapping;
    }

    set(type:CBARTextureType, texture:CBARTexture|undefined) {
        //only one of each type
        const existing = this.values[type];
        if (existing) {
            this.remove(existing)
        }
        if (texture) {
            texture.type = type;
            this.add(texture)
        }
    }

    private _textures: Record<string, CBARTexture> = {};

    public get values()  {
        return this._textures
    }

    public add(asset:CBARTexture) {
        this._textures[asset.type] = asset
    }

    public containsKey(key:string) : boolean {
        return this._textures.hasOwnProperty(key);
    }

    public remove(asset:CBARTexture) {
        if (!this.containsKey(asset.type)) return;
        this._textures[asset.type].unload();
        delete this._textures[asset.type]
    }

    public length(): number {
        return Object.keys(this._textures).length
    }

    public clearAll() {
        this.all().forEach(t=>{
            t.unload();
        });
        this._textures = {}
    }

    public first() {
        const length = this.length();
        if (length) {
            return this.all()[0];
        }
    }

    public last() {
        const length = this.length();
        if (length) {
            return this.all()[length-1];
        }
    }

    public all() {
        return Object.values(this._textures)
    }

    // @ts-ignore clean this up later
    load(basePath:string|undefined, json:CBARMaterialProperties) : Promise<CBARMaterial<T>> {

        const promises:Promise<CBARTexture>[] = [];

        if (json.ppcm) {
            this.ppcm = json.ppcm
        }

        if (json.ppi) {
            this.ppi = json.ppi;
        }

        this.scale = json.scale ? json.scale : 1.0;

        this.mirroredX = this.mirroredY = false;

        if (json.repeat === false) {
            this.wrappingX = this.wrappingY = THREE.ClampToEdgeWrapping;
        }
        else if (json.mirrored !== undefined) {
            this.mirroredX = this.mirroredY = json.mirrored;
        } else {
            if (json.mirroredX !== undefined) {
                this.mirroredX = json.mirroredX;
            }
            if (json.mirroredY !== undefined) {
                this.mirroredY = json.mirroredY;
            }
        }

        if (json.lightingFactor !== undefined) {
            this.lightingFactor = json.lightingFactor;
        }
        if (json.lightingOffset !== undefined) {
            this.lightingOffset = json.lightingOffset;
        }
        if (json.smoothStep !== undefined) {
            this.smoothStep = json.smoothStep;
        }

        let toRemove:string[] = [];
        if (json.textures) {
            toRemove = Object.keys(this._textures);

            for (const name in json.textures) {
                const index = toRemove.indexOf(name);
                let tex = this._textures[name];

                if (tex && index >=0) {
                    toRemove.splice(index, 1);
                } else {
                    tex = new CBARTexture(this.context, name as CBARTextureType);
                    this.add(tex);
                }
                promises.push(tex.load(basePath, json.textures[name], json.crop));
            }
        }

        if (json.properties) {
            for (const name in json.properties) {
                this.setMaterialProperty(name as CBARMaterialProperty, json.properties[name])
            }
        }


        return new Promise<CBARMaterial<T>>((resolve, reject)=>{

            if (promises.length) {
                Promise.all(promises).then((textures) => {

                    //remove previously used, and no longer in use
                    for (const remove of toRemove) {
                        const texture = this.values[remove];
                        texture.unload();
                        this.setTextureMap(texture);
                        this.remove(texture)
                    }

                    for (const texture of textures) {
                        const map = this.setTextureMap(texture);
                        if (map) {
                            //map.anisotropy = 16;
                        } else {
                            this.rejectPromise(reject, new CBARError(`Invalid texture type ${texture.type}`, CBARErrorCode.InvalidType, this));
                        }
                    }
                    resolve(this)
                }).catch((error) => {
                    reject(error)
                })
            } else {
                resolve(this)
            }
        })
    }

    public loadTexture(path:string, type:CBARTextureType, basePath?:string, crop?:ImageCrop) {
        const tex  = new CBARTexture(this.context, type);
        this.add(tex);
        return tex.load(basePath, path, crop);
    }

    data(): any {
        return {}
    }

    get description(): string {
        return "Material";
    }

    clone() {

        let copy = this.cloneObject();

        copy._material = this.threeMaterial.clone();

        copy.threeMaterial.onBeforeCompile = (shader) => {
            copy.onBeforeCompileCallback(shader)
        };

        return copy

    }

    public unload() {
        this.clearAll();
    }
}